import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getMyBranches,
  setMyBranches,
  addMyBranch,
  removeMyBranch,
  branchesKey,
  type Branch,
} from "@/api/service/me";
import { useAuth } from "@/hooks/useAuth";

/**
 * The user's branches, primary first.
 *
 * `branches` is `[]` both while loading and for a user who genuinely has
 * none, so callers that need to tell those apart must check `isLoading` —
 * rendering a "pick your branch" prompt during the first fetch would flash
 * on every page load.
 */
export function useBranches() {
  const { user } = useAuth();
  const query = useQuery({
    queryKey: branchesKey,
    queryFn: getMyBranches,
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
  });

  const branches: Branch[] = query.data?.data ?? [];
  return {
    ...query,
    branches,
    primary: branches[0] ?? null,
    /** True only once we know: loaded, signed in, and still empty. */
    needsBranch: !!user && !query.isLoading && branches.length === 0,
  };
}

/**
 * Writes. Every mutation returns the full new list, so the cache is seeded
 * from the response rather than invalidated — the nav pill and Settings
 * update in the same frame instead of flickering through a refetch.
 */
export function useBranchMutations() {
  const qc = useQueryClient();
  const seed = (res: { data?: Branch[] }) => {
    if (res.data) qc.setQueryData(branchesKey, res);
    // The home summary embeds the branch list and is keyed off the primary
    // branch, so it has to be refetched when the order changes.
    qc.invalidateQueries({ queryKey: ["home"] });
  };

  return {
    add: useMutation({ mutationFn: addMyBranch, onSuccess: seed }),
    remove: useMutation({ mutationFn: removeMyBranch, onSuccess: seed }),
    reorder: useMutation({ mutationFn: setMyBranches, onSuccess: seed }),
  };
}
